import posthog from 'posthog-js';

type SessionTiming = { access_token: string; expires_at?: number };
export function authDiagnosticStamp() {
  try { return { wall: Date.now(), mono: performance.now(), visibility: document.visibilityState === 'visible' ? 'visible' : 'hidden', online: navigator.onLine }; }
  catch { return null; } // Observation must never interrupt a request.
}
type Stamp = ReturnType<typeof authDiagnosticStamp>;
type Context = {
  operationId?: string; method: string; lookupStarted: Stamp; lookupCompleted: Stamp; sent?: Stamp; received?: Stamp;
  status?: number; transportAttempt?: number; authRetried?: boolean; responseDate?: string | null;
  refreshStarted?: Stamp; refreshCompleted?: Stamp; refreshOutcome?: 'failed' | 'succeeded' | 'missing_session'; tokenChanged?: boolean;
};
const numeric = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 8.64e12 ? value : null;
const difference = (end: number | null | undefined, start: number | null | undefined) => end == null || start == null ? null : end - start;

/** Only called for 401s and their existing recovery. Never validates or changes auth. */
export function captureV3AuthDiagnostic(stage: 'unauthorized' | 'refresh_completed' | 'retry_response', session: SessionTiming | null | undefined, context: Context) {
  try {
    let exp: number | null = null, iat: number | null = null;
    try {
      const parts = session?.access_token.split('.');
      if (parts?.length === 3 && parts[1].length <= 32_768) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        exp = numeric(payload?.exp); iat = numeric(payload?.iat);
      }
    } catch { /* Never emit the token, payload, or decoder error. */ }
    const { lookupStarted, lookupCompleted, sent, received, refreshStarted, refreshCompleted } = context;
    const build = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;
    const responseDate = numeric(Date.parse(context.responseDate || ''));
    // Existing PostHog identity/automatic metadata is unchanged; explicit fields are allowlisted.
    posthog.capture('v3_auth_diagnostic', {
      pipeline: 'generation_v3', diagnostic_version: 1, stage,
      read_operation_id: safeReadId(context.operationId),
      build_commit: build && /^[a-f0-9]{40}$/i.test(build) ? build : null,
      method: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(context.method) ? context.method : 'OTHER',
      jwt_exp_s: exp, jwt_iat_s: iat, session_expires_at_s: numeric(session?.expires_at),
      jwt_remaining_at_send_s: exp === null || !sent ? null : exp - sent.wall / 1000,
      jwt_age_at_send_s: iat === null || !sent ? null : sent.wall / 1000 - iat,
      auth_lookup_started_at_ms: lookupStarted?.wall ?? null, auth_lookup_completed_at_ms: lookupCompleted?.wall ?? null,
      auth_lookup_monotonic_ms: difference(lookupCompleted?.mono, lookupStarted?.mono),
      request_sent_at_ms: sent?.wall ?? null, response_received_at_ms: received?.wall ?? null,
      request_monotonic_ms: difference(received?.mono, sent?.mono),
      refresh_started_at_ms: refreshStarted?.wall ?? null, refresh_completed_at_ms: refreshCompleted?.wall ?? null,
      refresh_monotonic_ms: difference(refreshCompleted?.mono, refreshStarted?.mono),
      refresh_outcome: context.refreshOutcome ?? null, token_changed: context.tokenChanged ?? null,
      status: context.status ?? null, transport_attempt: context.transportAttempt ?? null, auth_retried: context.authRetried ?? null,
      visibility_at_send: sent?.visibility ?? 'unknown', visibility_at_response: received?.visibility ?? 'unknown',
      online_at_send: sent?.online ?? null, online_at_response: received?.online ?? null,
      // Same-origin response clock, not necessarily the backend clock; transit/suspension adds uncertainty.
      response_date_ms: responseDate, client_minus_response_date_ms: difference(received?.wall, responseDate),
    });
  } catch { /* Diagnostics must never interrupt authentication or requests. */ }
}

// A new random UUID per logical read, never derived from application identifiers.
const safeReadId = (id?: string) => id && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id) ? id : null;

/** Emit exactly one final outcome for reads that failed or required recovery.
 * Missing telemetry is unknown, not evidence of recovery. Raw 401s stay intact.
 */
export function captureV3ReadOutcome(operationId: string, outcome: 'recovered' | 'terminal' | 'cancelled', read: { attempts: number; status: number | null; unauthorized: boolean }) {
  try {
    posthog.capture('v3_read_outcome', {
      pipeline: 'generation_v3', diagnostic_version: 1,
      read_operation_id: safeReadId(operationId), outcome,
      attempts: read.attempts, last_http_status: read.status,
      saw_unauthorized: read.unauthorized,
    });
  } catch { /* Observability must never affect the request result. */ }
}
